import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useAuth from "../../hook/useAuth";
import { getUser, put_User } from "../../services/Users";
import EmailAlreadyExistChecking from "../../components/EmailAlreadyExistChecking";
import { getEmailChecking } from "../../services/EmailChecking";
import PasswordValidation from "../../components/PasswordValidation";
import UsernameValidatedComponent from "../../components/UsernameValidatedComponent";
import { getUsername } from "../../services/UsernameChecking";
import { formatUSPhone, handleUSPhoneChange } from "../../utils/phoneFormatter";
import { useUSPhoneValidation } from "../../hook/useUSPhoneValidation";
import { getFamilyMember, put_FamilyMember } from "../../services/FamilyMember"; import useGroup from "../../hook/useGroup";
import Cookies from "js-cookie";
import useConfirm from "../../hook/useConfirm";
import baseURL from "../../config/api";

const AdminMyAccount = () => {
  const token = Cookies.get("token-my-cdir");
  const navigate = useNavigate();
  const { auth }: any = useAuth();
  const { isSuperAdmin }: any = useGroup();
  const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
  const [userData, setUserData] = useState<any>({
    dateFormat: "",
    emailAddress: "",
    enabled: false,
    firstName: "",
    forcePasswordChange: false,
    groupId: 0,
    id: 0,
    language: "",
    lastName: "",
    mobilPhoneNumber: "",
    roleId: 0,
    userName: "",
  });
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isValidPassword, setIsValidPassword] = useState<boolean>(false);
  const [usernameTouched, setUsernameTouched] = useState<boolean>(false);
  const [emailTouched, setEmailTouched] = useState<boolean>(false);
  const [passwordTouched, setPasswordTouched] = useState<boolean>(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [oldPasswordTouched, setOldPasswordTouched] = useState<boolean>(false);
  const [isValidOldPassword, setIsValidOldPassword] = useState<boolean>(false);
  const {
    isValid: isPhoneValid,
    touched: phoneTouched,
    setTouched: setPhoneTouched
  } = useUSPhoneValidation(userData.mobilPhoneNumber);
  const [isUsernameChecking, setIsUsernameChecking] = useState<boolean>(false);
  const [isEmailChecking, setIsEmailChecking] = useState<boolean>(false);

  const hasFetched = useRef<boolean>(false);

  useEffect(() => {
    if (auth?.id && !hasFetched.current) {
      hasFetched.current = true;

      const apiCall = isSuperAdmin
        ? getUser(auth.id, token!.toString())
        : getFamilyMember(auth.id, token!.toString());

      apiCall
        .then((res) => {
          setUserData({
            ...res,
            mobilPhoneNumber: isSuperAdmin ? res.mobilPhoneNumber : res.phoneNumber,
          });
        })
        .catch((error) => console.error(error));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.id]);

  const closeModal = () => navigate(-1);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isUsernameChecking || isEmailChecking) return;
    const changingPassword = oldPassword.length > 0 || newPassword.length > 0;

    if (changingPassword) {
      setOldPasswordTouched(true);
      setPasswordTouched(true);

      if (!oldPassword || !newPassword) {
        setConfirmData({
          title: "Warning",
          summary: "Enter both your old password and new password to change your password.",
          confirmBtn: "Ok",
          isConfirm: (callback: any) => callback(),
        });
        setConfirmPopup(true);
        return;
      }

      if (!isValidOldPassword || !isValidPassword) return;

      if (oldPassword === newPassword) {
        setConfirmData({
          title: "Warning",
          summary: "New password must be different from the old password.",
          confirmBtn: "Ok",
          isConfirm: (callback: any) => callback(),
        });
        setConfirmPopup(true);
        return;
      }
    }

    if (!isProcessing) {
      setIsProcessing(true);

      try {
        if (changingPassword) {
          const endpoint = isSuperAdmin
            ? "users/DoesUserPasswordMatch"
            : "FamilyMember/DoesFamilyMemberPasswordMatch";

          const response = await fetch(
            `${baseURL}${endpoint}/${encodeURIComponent(oldPassword)},${auth.id}`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (!response.ok) {
            throw new Error("Unable to verify the old password.");
          }

          const result = await response.json();

          if (result !== true) {
            setConfirmData({
              title: "Warning",
              summary: "The old password is incorrect.",
              confirmBtn: "Ok",
              isConfirm: (callback: any) => callback(),
            });
            setConfirmPopup(true);
            return;
          }
        }

        const payload = {
          ...userData,
          ...(changingPassword ? { password: newPassword } : {}),
        };

        const apiCall = isSuperAdmin
          ? put_User(payload, token!.toString())
          : put_FamilyMember(payload, token!.toString());

        await apiCall;
        navigate(-1);
      } catch (error: any) {
        const errorMessage =
          error?.response?.data ||
          error?.message ||
          "Something went wrong, Please try again.";

        setConfirmData({
          title: "Warning",
          summary: errorMessage,
          confirmBtn: "Ok",
          isConfirm: (callback: any) => {
            callback();
          },
        });
        setConfirmPopup(true);
        console.error(error);
      } finally {
        setIsProcessing(false);
      }
    }
  };

  return (
    <div className="w-full">
      <div className="-mt-28 mb-6 px-4">
        <div className="mx-auto mt-[8rem] mb-12 max-w-6xl shadow-lg py-8 px-6 relative bg-white rounded">
          <div className="text-xl">Update Account</div>
          <form
            onSubmit={handleSubmit}
            className="mt-8 grid xm:grid-cols-2 gap-6 text-secondary"
          >
            <div>
              <label className="font-semibold text-sm">First Name</label>
              <input
                type="text"
                placeholder="Enter First Name"
                className="w-full rounded py-2.5 px-4 mt-2 border-2 text-sm text-black outline-secondary"
                value={userData.firstName}
                onChange={(e) =>
                  setUserData({ ...userData, firstName: e.target.value })
                }
                required
              />
            </div>
            <div>
              <label className="font-semibold text-sm">Last Name</label>
              <input
                type="text"
                placeholder="Enter Last Name"
                className="w-full rounded py-2.5 px-4 mt-2 border-2 text-sm text-black outline-secondary"
                value={userData.lastName}
                onChange={(e) =>
                  setUserData({ ...userData, lastName: e.target.value })
                }
                required
              />
            </div>

            <UsernameValidatedComponent
              userName={userData.userName}
              setUserName={(value) => setUserData({ ...userData, userName: value })}
              getUsername={getUsername}
              className={{
                parentDiv: "",
                label: "font-semibold text-sm",
                input: "w-full rounded py-2.5 px-4 mt-2 border-2 text-sm text-black outline-secondary",
              }}
              touched={usernameTouched}
              setTouched={setUsernameTouched}
              setIsChecking={setIsUsernameChecking}
              id={userData?.id}
            />

            <div>
              <PasswordValidation
                labelText="Old Password"
                password={oldPassword}
                setPassword={setOldPassword}
                isValid={isValidOldPassword}
                setIsValid={setIsValidOldPassword}
                className={{
                  label: "font-semibold text-sm",
                  div: "w-full text-sm relative",
                  input: "w-full rounded py-2.5 px-4 mt-2.5 border-2 text-sm text-black outline-secondary"
                }}
                touched={oldPasswordTouched}
                setTouched={setOldPasswordTouched}
              />
            </div>

            <EmailAlreadyExistChecking
              text={userData.emailAddress}
              setText={(value) => setUserData({ ...userData, emailAddress: value })}
              getEmailChecking={getEmailChecking}
              className={{
                parentDiv: "",
                label: "font-semibold text-sm",
                input: "w-full rounded py-2.5 px-4 border-2 mt-2 text-sm text-black outline-secondary",
              }}
              touched={emailTouched}
              setTouched={setEmailTouched}
              setIsChecking={setIsEmailChecking}
              id={userData?.id}
            />

            <div>
              <PasswordValidation
                labelText="New Password"
                password={newPassword}
                setPassword={setNewPassword}
                isValid={isValidPassword}
                setIsValid={setIsValidPassword}
                className={{
                  label: "font-semibold text-sm",
                  div: "w-full text-sm relative",
                  input: "w-full rounded py-2.5 px-4 mt-2.5 border-2 text-sm text-black outline-secondary"
                }}
                touched={passwordTouched}
                setTouched={setPasswordTouched}
              />
            </div>


            <div>
              <label className="font-semibold text-sm">Phone Number</label>
              <input
                type="tel"
                name="phone"
                value={formatUSPhone(userData.mobilPhoneNumber)}
                onChange={(e) => {
                  setPhoneTouched(true);
                  handleUSPhoneChange(e, (digits) =>
                    setUserData((prev: any) => ({
                      ...prev,
                      mobilPhoneNumber: digits,
                    }))
                  );
                }}
                required
                className="w-full rounded py-2.5 px-4 border-2 mt-2 text-sm text-black outline-secondary"
                placeholder="Phone No."
              />

              {!isPhoneValid && phoneTouched && (
                <p className="text-xs text-red-500 mt-1">
                  Please enter a valid 10-digit phone number
                </p>
              )}
            </div>
            <div>
              <label htmlFor="dateFormat" className="font-semibold text-sm">
                Date Format
              </label>
              <select
                name="dateFormat"
                required
                className="w-full rounded py-2.5 px-4 border-2 mt-2 text-sm text-black outline-secondary"
                value={userData.dateFormat}
                onChange={(e) => {
                  setUserData({ ...userData, dateFormat: e.target.value });
                }}
              >
                <option value="MM-dd-yyyy">MM-dd-yyyy</option>
              </select>
            </div>

            <div>
              <label htmlFor="language" className="font-semibold text-sm">
                Language
              </label>
              <select
                name="language"
                required
                className="w-full rounded py-2.5 px-4 border-2 mt-2 text-sm text-black outline-secondary"
                value={userData.language}
                onChange={(e) => {
                  setUserData({ ...userData, language: e.target.value });
                }}
              >
                <option value="English">English</option>
              </select>
            </div>

            <div />

            <div className="flex max-xm:flex-col gap-4 !mt-4">
              <button
                type="button"
                className="px-6 py-2 min-w-[150px] rounded text-black text-sm font-semibold border-none outline-none bg-gray-200 hover:bg-gray-300 active:bg-gray-200"
                onClick={closeModal}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 min-w-[150px] rounded text-white text-sm font-semibold border-none outline-none bg-secondary hover:bg-custom-blue"
                disabled={isUsernameChecking || isEmailChecking}
              >
                {
                  (isUsernameChecking || isEmailChecking || isProcessing)
                    ? (
                      <>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18px"
                          fill="#fff"
                          className="mr-2 inline animate-spin"
                          viewBox="0 0 26.349 26.35"
                        >
                          <circle
                            cx="13.792"
                            cy="3.082"
                            r="3.082"
                            data-original="#000000"
                          />
                          <circle
                            cx="13.792"
                            cy="24.501"
                            r="1.849"
                            data-original="#000000"
                          />
                          <circle
                            cx="6.219"
                            cy="6.218"
                            r="2.774"
                            data-original="#000000"
                          />
                          <circle
                            cx="21.365"
                            cy="21.363"
                            r="1.541"
                            data-original="#000000"
                          />
                          <circle
                            cx="3.082"
                            cy="13.792"
                            r="2.465"
                            data-original="#000000"
                          />
                          <circle
                            cx="24.501"
                            cy="13.791"
                            r="1.232"
                            data-original="#000000"
                          />
                          <path
                            d="M4.694 19.84a2.155 2.155 0 0 0 0 3.05 2.155 2.155 0 0 0 3.05 0 2.155 2.155 0 0 0 0-3.05 2.146 2.146 0 0 0-3.05 0z"
                            data-original="#000000"
                          />
                          <circle
                            cx="21.364"
                            cy="6.218"
                            r=".924"
                            data-original="#000000"
                          />
                        </svg>
                        Processing...
                      </>
                    ) : (
                      "Save"
                    )
                }
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminMyAccount;
